import React, { useState, useEffect } from 'react';
import { SaveAsOutlined, SaveOutlined } from '@mui/icons-material';
import { Archive, FileDigit, FileType2, Share, Clock, Copy, CopyCheck, Link2, Link2Off } from 'lucide-react';
import Modal from './Modal';

export const ReportSave = ({ selectedReport, selectedFields, selectedCriteria, saveReport, downloadExcel, downloadWord, downloadCSV, refreshReports, setMessage, initialAction = null, onInitialActionHandled = null, hideOptions = false }) => {
    const [newReportName, setNewReportName] = useState(selectedReport?.name || '');
    const [savingReport, setSavingReport] = useState(false);
    const [showSchedule, setShowSchedule] = useState(false);
    const [showShare, setShowShare] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [inputFocused, setInputFocused] = useState(false);
    const [scheduleSettings, setScheduleSettings] = useState(() => {
        return {
            frequency: selectedReport?.schedule_frequency || 'Off',
            timezone: selectedReport?.schedule_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            time_of_day: selectedReport?.schedule_time || 'Morning',
            days: (() => { try { return JSON.parse(selectedReport?.schedule_days || '[]'); } catch { return []; } })(),
            months: (() => { try { return JSON.parse(selectedReport?.schedule_months || '[]'); } catch { return []; } })(),
        };
    });
    const [shareSearch, setShareSearch] = useState('');
    const [users, setUsers] = useState([]);
    const [shared, setShared] = useState(() => { try { return JSON.parse(selectedReport?.shared || '[]'); } catch { return []; } });
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const [shareLink, setShareLink] = useState(null);
    const [shareLinkBusy, setShareLinkBusy] = useState(false);
    const [shareLinkMessage, setShareLinkMessage] = useState('');
    const [shareLinkCopied, setShareLinkCopied] = useState(false);

    // Sync incoming selectedReport changes into local state
    useEffect(() => {
        setNewReportName(selectedReport?.name || '');
        setShared(() => { try { return JSON.parse(selectedReport?.shared || '[]'); } catch { return []; } });
        setScheduleSettings(s => ({
            ...s,
            frequency: selectedReport?.schedule_frequency || 'Off',
            timezone: selectedReport?.schedule_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            time_of_day: selectedReport?.schedule_time || 'Morning',
            days: (() => { try { return JSON.parse(selectedReport?.schedule_days || '[]'); } catch { return []; } })(),
            months: (() => { try { return JSON.parse(selectedReport?.schedule_months || '[]'); } catch { return []; } })(),
        }));
    }, [selectedReport]);

    useEffect(() => {
        if (showShare) {
            (async () => {
                try {
                    const res = await fetch(`https://api.casedb.co/user.php?users=true&time=${Date.now()}`, { credentials: 'include' });
                    const data = await res.json();
                    if (data.users) setUsers(data.users);
                } catch (e) { console.error(e); }
            })();
        }
    }, [showShare]);

    useEffect(() => {
        if (!showShare || !selectedReport?.id) return;

        (async () => {
            setShareLinkMessage('');
            try {
                const response = await fetch('https://api.casedb.co/report-update.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'get_share_link', report_id: selectedReport.id }),
                    credentials: 'include'
                });
                const data = await response.json();
                if (data.success) setShareLink(data.share_link || null);
            } catch (e) {
                console.error(e);
            }
        })();
    }, [showShare, selectedReport?.id]);

    useEffect(() => {
        if (!initialAction) return;

        if (initialAction === 'share') {
            setShowShare(true);
        } else if (initialAction === 'schedule') {
            setShowSchedule(true);
        }

        onInitialActionHandled && onInitialActionHandled();
    }, [initialAction, onInitialActionHandled]);

    const usTimezones = [
        'America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Phoenix','America/Anchorage','Pacific/Honolulu'
    ];

    const toggleArrayVal = (arr, val) => arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

    const saveSchedule = async () => {
        const response = await fetch('https://api.casedb.co/report-update.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'update_schedule', report_id: selectedReport.id, schedule: scheduleSettings }),
            credentials: 'include'
        });
        const data = await response.json();
        setShowSchedule(false); refreshReports && refreshReports();
        setMessage && setMessage({ text: data.message || 'Schedule updated', type: data.success ? 'success' : 'error' });
    };

    const saveShares = async () => {
        const response = await fetch('https://api.casedb.co/report-update.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'update_share', report_id: selectedReport.id, shared }),
            credentials: 'include'
        });
        const data = await response.json();
        setShowShare(false); refreshReports && refreshReports();
        setMessage && setMessage({ text: data.message || 'Share list updated', type: data.success ? 'success' : 'error' });
    };

    const createShareLink = async () => {
        setShareLinkBusy(true);
        setShareLinkMessage('');
        try {
            const response = await fetch('https://api.casedb.co/report-update.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'create_share_link', report_id: selectedReport.id }),
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                setShareLink(data.share_link || null);
                setShareLinkMessage(data.message || 'Share link created');
            } else {
                setShareLinkMessage(data.message || 'Failed to create share link');
            }
        } catch (e) {
            setShareLinkMessage('Failed to create share link');
        } finally {
            setShareLinkBusy(false);
        }
    };

    const revokeShareLink = async () => {
        setShareLinkBusy(true);
        setShareLinkMessage('');
        try {
            const response = await fetch('https://api.casedb.co/report-update.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'revoke_share_link', report_id: selectedReport.id }),
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                setShareLink(null);
                setShareLinkMessage(data.message || 'Share link revoked');
            } else {
                setShareLinkMessage(data.message || 'Failed to revoke share link');
            }
        } catch (e) {
            setShareLinkMessage('Failed to revoke share link');
        } finally {
            setShareLinkBusy(false);
        }
    };

    const copyShareLink = async () => {
        if (!shareLink?.url) return;
        try {
            await navigator.clipboard.writeText(shareLink.url);
            setShareLinkMessage('Share link copied');
            setShareLinkCopied(true);
            setTimeout(() => setShareLinkCopied(false), 1600);
        } catch (e) {
            setShareLinkMessage('Copy failed. You can copy the link manually.');
        }
    };

    const archiveReport = async () => {
        const response = await fetch('https://api.casedb.co/report-update.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'archive', report_id: selectedReport.id }),
            credentials: 'include'
        });
        const data = await response.json();
        refreshReports && refreshReports();
        setMessage && setMessage({ text: data.message || 'Report archived', type: data.success ? 'success' : 'error' });
    };

    const reportUnchanged = (selectedReport, selectedFields, selectedCriteria) => {
        const reportFields = typeof selectedReport?.fields === 'string' 
            ? JSON.parse(selectedReport.fields || '[]')
            : selectedReport?.fields || [];
        const currentFieldIds = selectedFields.filter(Boolean).map(field => field.id);

        const arraysMatch = (a, b) => {
            if (a.length !== b.length) return false;

            const setA = new Set(a);
            const setB = new Set(b);

            for (const val of setA) {
                if (!setB.has(val)) return false;
            }

            return true;
        };

        const deepEqual = (a, b) => {
            try {
                return JSON.stringify(a, Object.keys(a).sort()) === JSON.stringify(b, Object.keys(b).sort());
            } catch (e) {
                return false;
            }
        };

        const reportCriteria = JSON.parse(selectedReport?.criteria || '{}');

        const fieldsMatch = arraysMatch(reportFields, currentFieldIds);
        const criteriaMatch = deepEqual(reportCriteria, selectedCriteria);

        return fieldsMatch && criteriaMatch;
    };

    const isUnchanged = reportUnchanged(selectedReport, selectedFields, selectedCriteria);

    const renderReportOptions = () => {
        if (Number(selectedReport?.premade) === 1) {
            return (
                <div className='report-save'>
                    <div className='report-save-action'>
                        <button className='action quaternary' onClick={() => setSavingReport(true)}>
                            <SaveAsOutlined />
                            Save As
                        </button>
                        <span className='subtext'>Save a copy of '{selectedReport?.name || 'Report'}' report design to your My Reports</span>
                    </div>
                    <div className='divider horizontal'></div>
                    <span className='subtext'>Export Report</span>
                    <div className='report-save-action'>
                        <div className='this-or-that-comp'>
                            <div
                                className={`tot-comp-option`}
                                title='Minimize document view'
                                onClick={downloadExcel}
                            >
                                <FileDigit size={18} />Excel
                            </div>
                            <div
                                className={`tot-comp-option`}
                                title='Expand document view'
                                onClick={downloadWord}
                            >
                                <FileType2 size={18} />Word
                            </div>
                        </div>
                        <span className='subtext'>Generate a link to download this report for Excel or Word.</span>
                    </div>
                </div>
            );
        } else {
            return (
                <div className='report-save'>
                    <div className='report-save-action'>
                        <button 
                            className='action quaternary'  
                            onClick={() => {
                                    saveReport(newReportName, 'save');
                                    setSavingReport(false);
                                }}
                            disabled={isUnchanged}
                        >
                            <SaveOutlined />
                            Save
                        </button>
                        <span className='subtext'>Save '{selectedReport?.name || 'Report'}' report design</span>
                    </div>
                    <div className='report-save-action'>
                        <button className='action quaternary' onClick={() => setSavingReport(true)}>
                            <SaveAsOutlined />
                            Save As
                        </button>
                        <span className='subtext'>Save a copy of '{selectedReport?.name || 'Report'}' report design to your My Reports</span>
                    </div>
                    <div className='divider horizontal'></div>
                    <span className='subtext'>Export Settings</span>
                    <div className='report-save-action'>
                        <div className='this-or-that-comp'>
                            <div
                                className={`tot-comp-option`}
                                title='Minimize document view'
                                onClick={() => downloadExcel()}
                            >
                                <FileDigit size={18} />Excel
                            </div>
                            <div
                                className={`tot-comp-option`}
                                title='Expand document view'
                                onClick={downloadWord}
                            >
                                <FileType2 size={18} />Word
                            </div>
                        </div>
                        <span className='subtext'>Generate a link to download this report for Excel or Word.</span>
                    </div>
                    {/*<div className='report-save-action'>
                        <button className='action quaternary'>
                            Export Options
                        </button>
                        <span className='subtext'>Download the default export template and upload your own.</span>
                    </div>*/}
                    <div className='report-save-action'>
                        <button className='action quaternary' onClick={downloadCSV}>
                            CSV Report
                        </button>
                        <span className='subtext'>Generate this report as CSV. When the report is ready it will be available for download in the saved reports section.</span>
                    </div>
                    <div className='divider horizontal'></div>
                    <div className='report-save-action'>
                        <button className='action quaternary' onClick={() => setShowSchedule(true)}>
                            <Clock size={18}/>Auto-report Settings
                        </button>
                        <span className='subtext'>Set up a schedule for this report to be automatically generated and sent to your email address.</span>
                    </div>
                    <div className='divider horizontal'></div>
                    <div className='report-save-action'>
                        <button className='action quaternary' onClick={() => setShowShare(true)}>
                            <Share size={18}/>Share Report
                        </button>
                        <span className='subtext'>Share this report type with individual users or all the users in an org. Other users will only see report data from projects they have access to.</span>
                    </div>
                    <div className='divider horizontal'></div>
                    <div className='report-save-action'>
                        <button className='action quaternary' onClick={archiveReport}>
                            <Archive size={18}/>Archive Report
                        </button>
                        <span className='subtext'>Remove this report from your My Reports and unsubscribe from this report's auto-reporting schedule (if set up). You can unarchive this report at any time from this step of the Report Builder.</span>
                    </div>
                </div>
            );
        }
    };

    return (
        <>
            {!hideOptions && renderReportOptions()}
            {savingReport && (
                <Modal
                    title="Save Report As"
                    onClose={() => setSavingReport(false)}
                    footer={
                        <div className='modal-footer-actions'>
                            <button className='action alt' onClick={() => setSavingReport(false)}>
                                Close
                            </button>
                            <button 
                                className='action' 
                                onClick={() => {
                                    saveReport(newReportName, 'save_as');
                                    setSavingReport(false);
                                }
                            }>
                                Save
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <div className='form-group'>
                            <label className='subtext'>Report Name<span className='required'>*</span></label>
                            <input
                                type='text'
                                className='form-control'
                                value={newReportName}
                                onChange={(e) => setNewReportName(e.target.value)}
                                placeholder='Enter report name'
                            />
                        </div>
                    </div>
                </Modal>
            )}
            {showSchedule && (
                <Modal title="Auto-report Settings" onClose={() => setShowSchedule(false)} footer={
                    <div className='modal-footer-actions'>
                        <button className='action alt' onClick={() => setShowSchedule(false)}>Close</button>
                        <button className='action' onClick={saveSchedule}>Save</button>
                    </div>
                }>
                    <div className='modal-content-wrapper'>
                        <div className='form-group full'>
                            <label className='subtext'>Schedule Frequency</label>
                            <select className='default-select' value={scheduleSettings.frequency} onChange={e => setScheduleSettings(s => ({...s, frequency: e.target.value}))}>
                                {['Off','Daily','Weekly','Monthly','Yearly'].map(f => <option key={f}>{f}</option>)}
                            </select>
                        </div>
                        {scheduleSettings.frequency !== 'Off' && (
                            <>
                            <div className='form-group full'>
                                <label className='subtext'>Select Timezone</label>
                                <select className='default-select' value={scheduleSettings.timezone} onChange={e => setScheduleSettings(s => ({...s, timezone: e.target.value}))}>
                                    {usTimezones.map(z => <option key={z} value={z}>{z}</option>)}
                                </select>
                            </div>
                            <div className='form-group full'>
                                <label className='subtext'>Report run time</label>
                                <div className='this-or-that-comp'>
                                    {['Morning','Evening'].map(t => (
                                        <div key={t} className={`tot-comp-option ${scheduleSettings.time_of_day===t?'active':''}`} onClick={() => setScheduleSettings(s => ({...s, time_of_day: t}))}>{t}</div>
                                    ))}
                                </div>
                            </div>
                            {scheduleSettings.frequency === 'Weekly' && (
                                <div className='form-group full'>
                                    <label className='subtext'>Select Days for Delivery</label>
                                    <div className='weekday-select'>
                                        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d,i) => (
                                            <div key={d} className={`weekday ${scheduleSettings.days.includes(i)?'active':''}`} onClick={() => setScheduleSettings(s => ({...s, days: toggleArrayVal(s.days, i)}))}>{d}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {scheduleSettings.frequency === 'Monthly' && (
                                <div className='form-group full'>
                                    <label className='subtext'>Select Days of Month</label>
                                    <div className='monthday-grid'>
                                        {Array.from({length:31},(_,i)=>i+1).map(day => (
                                            <div key={day} className={`monthday ${scheduleSettings.days.includes(day)?'active':''}`} onClick={() => setScheduleSettings(s => ({...s, days: toggleArrayVal(s.days, day)}))}>{day}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {scheduleSettings.frequency === 'Yearly' && (
                                <div className='form-group full'>
                                    <label className='subtext'>Select Months</label>
                                    <div className='month-select'>
                                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m,i) => (
                                            <div key={m} className={`month ${scheduleSettings.months.includes(i)?'active':''}`} onClick={() => setScheduleSettings(s => ({...s, months: toggleArrayVal(s.months, i)}))}>{m}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            </>
                        )}
                    </div>
                </Modal>
            )}
            {showShare && (
                <Modal
                    title="Share Report With..."
                    onClose={() => setShowShare(false)}
                    wide
                    overflowy={true}
                    footer={
                        <div className='modal-footer-actions'>
                            <button className='action alt' onClick={() => setShowShare(false)}>Close</button>
                            <button className='action' onClick={saveShares}>Save</button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <div className='share-container'>
                            <div className='shareable form-group' style={{ position: 'relative'}}>
                                <label className='subtext'>Add Users</label>
                                <div 
                                    className='autocomplete-wrapper'
                                    onMouseEnter={() => setShowHint(true)}
                                    onMouseLeave={() => setShowHint(false)}
                                >
                                    <input
                                        className='form-control'
                                        placeholder='Type to search users...'
                                        value={shareSearch}
                                        onFocus={() => { setShowUserDropdown(true); setInputFocused(true); }}
                                        onBlur={() => { setTimeout(()=> setShowUserDropdown(false), 150); setInputFocused(false); setShowHint(false); }}
                                        onChange={e => { setShareSearch(e.target.value); setHighlightIndex(0); setShowUserDropdown(true); }}
                                        onKeyDown={e => {
                                            const filtered = users.filter(u =>
                                                (!shareSearch || u.name.toLowerCase().includes(shareSearch.toLowerCase()) || String(u.id) === shareSearch)
                                                && !shared.includes(u.id)
                                            ).slice(0, 15);
                                            if (e.key === 'ArrowDown') {
                                                e.preventDefault();
                                                setHighlightIndex(i => (i + 1) % Math.max(filtered.length, 1));
                                            } else if (e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                setHighlightIndex(i => (i - 1 + Math.max(filtered.length,1)) % Math.max(filtered.length,1));
                                            } else if (e.key === 'Enter') {
                                                e.preventDefault();
                                                const pick = filtered[highlightIndex];
                                                if (pick) { setShared(s => [...s, pick.id]); setShareSearch(''); setShowHint(false); }
                                            } else if (e.key === 'Escape') {
                                                setShowUserDropdown(false);
                                            }
                                        }}
                                    />
                                    {showUserDropdown && (
                                        <div className='autocomplete-dropdown'>
                                            {(() => {
                                                const filtered = users.filter(u =>
                                                    (!shareSearch || u.name.toLowerCase().includes(shareSearch.toLowerCase()) || String(u.id) === shareSearch)
                                                    && !shared.includes(u.id)
                                                ).slice(0, 15);
                                                if (filtered.length === 0) return <div className='dropdown-empty'>No matches</div>;
                                                return filtered.map((u, idx) => (
                                                    <div
                                                        key={u.id}
                                                        className={`dropdown-item ${idx===highlightIndex?'highlight':''}`}
                                                        onMouseDown={() => { setShared(s => [...s, u.id]); setShareSearch(''); setShowHint(false); }}
                                                    >
                                                        {u.name} <span className='subtext'>(@{u.user})</span>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    )}
                                    {showHint && !inputFocused && (
                                        <div className='hint small-text report-share-hint'>Type a name or ID, press Enter to add.<br/>Click a selected user to remove.</div>
                                    )}
                                </div>
                            </div>
                            <div className='shared-with'>
                                <div className='share-list form-group' style={{ width: '100%', maxWidth: '100%' }}>
                                    <label className='subtext'>Shared with</label>
                                    {shared.map((id, index) => {
                                        const u = users.find(us => us.id === id);
                                        return <div key={id} className={`share-item ${index === shared.length - 1 ? 'active' : ''}`} onClick={()=> setShared(s=> s.filter(v=> v!==id))}>{u?u.name:id}</div>
                                    })}
                                    {shared.length === 0 && <div className='share-item'>No users selected</div>}
                                </div>
                            </div>
                        </div>
                        <div className='divider horizontal' style={{ margin: '10px 0' }}></div>
                        <div className='report-share-link-panel'>
                            <div className='report-share-link-details'>
                                <label className='subtext'>Authenticated Share Link</label>
                                <span className='subtext' style={{ whiteSpace: 'pre-wrap' }}>Anyone with this link must still sign in, and report data is generated from their session permissions.</span>
                            </div>
                            {shareLink?.url ? (
                                <div className='report-share-link-row'>
                                    <input className='form-control' value={shareLink.url} readOnly />
                                    <button className='action-micro quaternary' style={{ maxWidth: '200px' }} type='button' onClick={copyShareLink} title='Copy share link' aria-label='Copy share link'>
                                        {shareLinkCopied ? <CopyCheck size={18} /> : <Copy size={18} />}
                                    </button>
                                    <button className='action-micro destroy' style={{ maxWidth: '200px' }} type='button' onClick={revokeShareLink} disabled={shareLinkBusy} title='Revoke share link' aria-label='Revoke share link'>
                                        <Link2Off size={18} />
                                    </button>
                                </div>
                            ) : (
                                <button className='action-micro tertiary' style={{ maxWidth: '200px' }} type='button' onClick={createShareLink} disabled={shareLinkBusy} title='Create share link' aria-label='Create share link'>
                                    <Link2 size={18} />
                                </button>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};